import { DiscountCodeBase } from '../../../contracts/discount-code.js';
import { fromEnumAsString, fromEnumAsStringOrNull, toEnumFromString, toEnumFromStringOrNull } from '../../../serialization/enum-utils.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { VersionRange } from '../../core/version-range.js';
import { cloneDataOrNull, DataModel, DataModelBuilder, isSameData, isSameList, SerializationContext, SerializedData } from '../../dto/dto.js';
import { BusinessModuleRef } from '../business-module-ref.js';
import { CustomerProfile } from '../customer-profile.js';
import { FormSubmission } from '../form-submission/form-submission.js';
import { InvoiceType } from '../invoicing/invoice-type.js';
import { Language } from '../language.js';
import { OrderDomain } from '../order-domain.js';
import { PaymentMethod } from '../payment-method.js';
import { PaymentType } from '../payment-type.js';
import { Price } from '../price.js';
import { CartCustomerDto } from './cart-customer.js';
import { CartItemDto } from './cart-item.js';
import { DonationCartItemDto } from './donation-cart-item.js';
import { OrderCreationStrategy } from './order-creation-strategy.js';

export class OnlineCartDto extends DataModel {
  static readonly TYPE_ID: string = '16ea414b67bc4172838bdef9ceb500d9';
  static readonly SCHEMA_VERSION: number = 13;

  private constructor(
      public readonly businessRef: BusinessModuleRef,
      public readonly eventId: string | null,
      public readonly items: CartItemDto[],
      public readonly voucher: DiscountCodeBase | null,
      public readonly customerId: string | null,
      public readonly customer: CartCustomerDto | null,
      public readonly paymentType: PaymentType | null,
      public readonly paymentMethod: PaymentMethod | null,
      public readonly invoiceType: InvoiceType | null,
      public readonly allowPlatformMarketing: boolean | null,
      public readonly allowBusinessMarketing: boolean | null,
      public readonly language: Language,
      public readonly formSubmissions: FormSubmission[] | null,
      public readonly domain: OrderDomain,
      public readonly strategy: OrderCreationStrategy,
  ) {
    super();
  }

  get $typeId() {
    return OnlineCartDto.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    // Version should always be `exactly`.
    // Cart should never be backwards migrated, backend should be updated before frontend.
    return VersionRange.exactly(OnlineCartDto.SCHEMA_VERSION);
  }

  static create(args: {
    businessRef: BusinessModuleRef,
    eventId: string | null,
    items: CartItemDto[],
    voucher: DiscountCodeBase | null,
    customerId: string | null,
    customer: CartCustomerDto | null,
    paymentType: PaymentType | null,
    paymentMethod: PaymentMethod | null,
    invoiceType: InvoiceType | null,
    allowPlatformMarketing: boolean | null,
    allowBusinessMarketing: boolean | null,
    language: Language,
    formSubmissions: FormSubmission[] | null,
    domain: OrderDomain,
    strategy: OrderCreationStrategy,
  }): OnlineCartDto {
    return new OnlineCartDto(
        args.businessRef,
        args.eventId,
        args.items,
        args.voucher,
        args.customerId,
        args.customer,
        args.paymentType,
        args.paymentMethod,
        args.invoiceType,
        args.allowPlatformMarketing,
        args.allowBusinessMarketing,
        args.language,
        args.formSubmissions,
        args.domain,
        args.strategy,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): OnlineCartDto {
    return new OnlineCartDto(
        BusinessModuleRef.mustDecode(MapUtils.getString(map, 'businessRef')),
        MapUtils.getStringOrNull(map, 'eventId'),
        MapUtils.getList<CartItemDto>(map, 'items', (e) => {
          return context.fromData(e as SerializedData);
        }),
        context.fromDataOrNull(MapUtils.getRecordOrNull(map, 'voucher')),
        MapUtils.getStringOrNull(map, 'customerId'),
        context.fromDataOrNull(MapUtils.getRecordOrNull(map, 'customer')),
        toEnumFromStringOrNull(MapUtils.getStringOrNull(map, 'paymentType'), PaymentType),
        toEnumFromStringOrNull(MapUtils.getStringOrNull(map, 'paymentMethod'), PaymentMethod),
        toEnumFromStringOrNull(MapUtils.getStringOrNull(map, 'invoiceType'), InvoiceType),
        MapUtils.getBooleanOrNull(map, 'allowPlatformMarketing'),
        MapUtils.getBooleanOrNull(map, 'allowBusinessMarketing'),
        toEnumFromString(MapUtils.getString(map, 'language'), Language),
        MapUtils.getListOrNull<FormSubmission>(map, 'formSubmissions', (e) => context.fromData<FormSubmission>(e as SerializedData)),
        toEnumFromString(MapUtils.getString(map, 'domain'), OrderDomain),
        toEnumFromString(MapUtils.getString(map, 'strategy'), OrderCreationStrategy),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'businessRef': this.businessRef.encode(),
      'eventId': this.eventId,
      'items': this.items.map((e) => context.toData(e)),
      'voucher': context.toDataOrNull(this.voucher),
      'customerId': this.customerId,
      'customer': context.toDataOrNull(this.customer),
      'paymentType': fromEnumAsStringOrNull(this.paymentType),
      'paymentMethod': fromEnumAsStringOrNull(this.paymentMethod),
      'invoiceType': fromEnumAsStringOrNull(this.invoiceType),
      'allowPlatformMarketing': this.allowPlatformMarketing,
      'allowBusinessMarketing': this.allowBusinessMarketing,
      'language': fromEnumAsString(this.language),
      'formSubmissions': this.formSubmissions?.map((e) => context.toData(e)) ?? null,
      'domain': fromEnumAsString(this.domain),
      'strategy': fromEnumAsString(this.strategy),
    };
  }

  clone(): this {
    return new OnlineCartDto(
        this.businessRef.clone(),
        this.eventId,
        this.items.map((e) => e.clone()),
        cloneDataOrNull(this.voucher),
        this.customerId,
        cloneDataOrNull(this.customer),
        this.paymentType,
        this.paymentMethod,
        this.invoiceType,
        this.allowPlatformMarketing,
        this.allowBusinessMarketing,
        this.language,
        this.formSubmissions?.map((e) => e.clone()) ?? null,
        this.domain,
        this.strategy,
    ) as this;
  }

  isSame(other: OnlineCartDto): boolean {
    if(other === this) {
      return true;
    }

    return isSameData(other.businessRef, this.businessRef) &&
      isSameList(other.items, this.items, isSameData) &&
      isSameData(other.voucher, this.voucher) &&
      other.eventId === this.eventId &&
      other.customerId === this.customerId &&
      other.paymentType === this.paymentType &&
      other.paymentMethod === this.paymentMethod &&
      other.language === this.language &&
      other.invoiceType === this.invoiceType &&
      other.allowPlatformMarketing === this.allowPlatformMarketing &&
      other.allowBusinessMarketing === this.allowBusinessMarketing &&
      other.domain === this.domain &&
      other.strategy === this.strategy &&
      isSameData(other.customer, this.customer) &&
      isSameList(other.formSubmissions, this.formSubmissions, isSameData);
  }

  static verifyType(order: DataModel): boolean {
    return order instanceof OnlineCartDto;
  }

  static assertType(order: DataModel): order is OnlineCartDto {
    return order instanceof OnlineCartDto;
  }
}

export class OnlineCartDtoBuilder extends DataModelBuilder<OnlineCartDto> {
  build(context: SerializationContext, map: SerializedData): OnlineCartDto {
    return OnlineCartDto.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(OnlineCartDto.SCHEMA_VERSION)) {
      return map;
    }

    if(versionRange.isGreaterThan(OnlineCartDto.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In version 2 we added `donation` of type `Price | null`.
    if(version < 2) version = 2;

    // In version 3 we added `language` of type `Language`.
    if(version < 3) {
      map['language'] = fromEnumAsString(Language.English);
      version = 3;
    }

    // In version 4 we added `formSubmissions` of type `TextFormEntry[] | null`.
    if(version < 4) version = 4;

    // In version 5 we added `strategy` of type `OrderCreationStrategy`.
    if(version < 5) {
      map['strategy'] = fromEnumAsString(OrderCreationStrategy.GenericOrder);
      version = 5;
    }

    // In version 6 donation was converted to a cart item.
    if(version < 6) {
      const donationPrice = Price.decodeOrNull(MapUtils.getOrNull<string>(map, 'donation'));
      if(donationPrice != null) {
        const items = MapUtils.getList<CartItemDto>(map, 'items', (e) => {
          return context.fromData(e as SerializedData);
        });

        items.push(DonationCartItemDto.create({
          amount: donationPrice,
        }));

        map['items'] = items.map((e) => context.toData(e));
      }

      version = 6;
    }

    // In version 7 we added `domain` of type `OrderDomain`.
    if(version < 7) {
      map['domain'] = fromEnumAsString(OrderDomain.inTime);
      version = 7;
    }

    // In version 8 voucher type was changed from `Voucher` to `DiscountCodeBase`.
    // No migration is required, class `Voucher` extends class `DiscountCodeBase`.
    if(version < 8) {
      version = 8;
    }

    // In version 9 we made `eventId` nullable.
    if(version < 9) version = 9;

    // In version 10 we made `paymentType` and `paymentMethod` nullable.
    if(version < 10) version = 10;

    /*
    * In version 11:
    * - we added `customerId` of type `string | null`.
    * - we changed `customer` from type `CustomerProfile` to `CartCustomerDto | null`.
    */
    if(version < 11) {
      const legacyCustomer = context.fromData<CustomerProfile>(MapUtils.getRecord(map, 'customer'));
      if(legacyCustomer.userId != null) {
        map['customerId'] = legacyCustomer.uniqueId;
        map['customer'] = null;
      }
      else {
        const result = CartCustomerDto.fromCustomer(legacyCustomer);
        map['customerId'] = null;
        map['customer'] = result.isOk ? context.toData(result.value) : null;
      }

      version = 11;
    }

    // In version 12 we removed `isGuestOrder`.
    if(version < 12) version = 12;

    // In version 13 we removed `creatorId`.
    if(version < 13) version = 13;

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
